import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { changePassword } from '../services/auth'
import { MIN_PASSWORD_LENGTH } from './password-change-modal.parts'

export interface PasswordChangeFormState {
  currentPassword: string
  setCurrentPassword: (value: string) => void
  newPassword: string
  setNewPassword: (value: string) => void
  confirmPassword: string
  setConfirmPassword: (value: string) => void
  showCurrentPassword: boolean
  toggleShowCurrentPassword: () => void
  showNewPassword: boolean
  toggleShowNewPassword: () => void
  showConfirmPassword: boolean
  toggleShowConfirmPassword: () => void
  isSubmitting: boolean
  error: string | null
  isNewPasswordValid: boolean
  doPasswordsMatch: boolean
  isFormValid: boolean
  hasMismatch: boolean
  handleSubmit: (e: React.FormEvent) => Promise<void>
}

function classifyError(t: ReturnType<typeof useTranslation<'auth'>>['t'], err: unknown): string {
  if (err instanceof Error) {
    if (err.message.includes('incorrect') || err.message.includes('invalid')) {
      return t('passwordChange.incorrectPassword')
    }
    return err.message
  }
  return t('passwordChange.failedError')
}

export function usePasswordChangeForm(onPasswordChanged: () => void): PasswordChangeFormState {
  const { t } = useTranslation('auth')
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isNewPasswordValid = newPassword.length >= MIN_PASSWORD_LENGTH
  const doPasswordsMatch = newPassword === confirmPassword && confirmPassword.length > 0
  const isFormValid = currentPassword.length > 0 && isNewPasswordValid && doPasswordsMatch
  const hasMismatch = confirmPassword.length > 0 && !doPasswordsMatch

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    if (!isFormValid) return
    setIsSubmitting(true)
    setError(null)
    try {
      await changePassword({ currentPassword, newPassword })
      onPasswordChanged()
    } catch (err) {
      setError(classifyError(t, err))
    } finally {
      setIsSubmitting(false)
    }
  }

  return {
    currentPassword,
    setCurrentPassword,
    newPassword,
    setNewPassword,
    confirmPassword,
    setConfirmPassword,
    showCurrentPassword,
    toggleShowCurrentPassword: () => {
      setShowCurrentPassword((prev) => !prev)
    },
    showNewPassword,
    toggleShowNewPassword: () => {
      setShowNewPassword((prev) => !prev)
    },
    showConfirmPassword,
    toggleShowConfirmPassword: () => {
      setShowConfirmPassword((prev) => !prev)
    },
    isSubmitting,
    error,
    isNewPasswordValid,
    doPasswordsMatch,
    isFormValid,
    hasMismatch,
    handleSubmit,
  }
}
